/**
 * CoachJeuxPage — Page dédiée au profil "coach_jeux".
 *
 * - Onglet "Nouveau bon" : panier multi-lignes (un bon peut contenir plusieurs jeux/sessions).
 *   Pour chaque ligne : Jeu, Nb parties, Prix unitaire (modifiable), Durée (option), Note ligne (option).
 *   Champs globaux du bon : Joueurs (texte libre), Notes générales.
 * - Onglet "Mes bons" : historique avec statut (pending/attached/invoiced/rejected).
 *
 * Une fois transmis, le bon est verrouillé en lecture seule.
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Gamepad2, Send, History, RefreshCw, Loader2, LogOut, Users,
  Clock, CheckCircle2, XCircle, Receipt, Hash, Coins, Plus, Trash2, FileText, Activity,
  Lock, Bell, Trophy, KeyRound,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import DailyReportPanel from "../components/DailyReportPanel";
import PlayersTrackerTab from "./coach/PlayersTrackerTab";
import JournalTab from "./coach/JournalTab";
import RecoupementSyntheseTable from "./caisse/components/RecoupementSyntheseTable";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEFAULT_HOURLY_RATE = 12000;  // 12 000 F / heure (forfait global)

const STATUS_META = {
  pending:  { label: "En attente",       color: "bg-amber-500/30 text-amber-200 border-amber-500/40",   icon: Clock },
  attached: { label: "Rattaché à table", color: "bg-blue-500/30 text-blue-200 border-blue-500/40",     icon: Hash },
  invoiced: { label: "Facturé direct",   color: "bg-emerald-500/30 text-emerald-100 border-emerald-500/40", icon: Receipt },
  rejected: { label: "Refusé",           color: "bg-rose-500/30 text-rose-100 border-rose-500/40",      icon: XCircle },
};

const CoachJeuxPage = ({ currentUser, onLogout }) => {
  const [activeTab, setActiveTab] = useState("players");

  // --- Catalog ---
  const [catalog, setCatalog] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  // --- Bon (panier) ---
  const [cart, setCart] = useState([]); // [{jeu_product_id, jeu_name, parties, unit_price, duration_minutes, notes}]
  const [players, setPlayers] = useState("");
  const [bonNotes, setBonNotes] = useState("");

  // --- Form add line ---
  const [billingMode, setBillingMode] = useState("parties"); // "parties" | "hourly"
  const [lineJeu, setLineJeu] = useState("");
  const [lineParties, setLineParties] = useState(1);
  const [linePrice, setLinePrice] = useState(0);
  const [lineHours, setLineHours] = useState(1);
  const [lineHourlyRate, setLineHourlyRate] = useState(DEFAULT_HOURLY_RATE);
  const [lineDuration, setLineDuration] = useState("");
  const [lineNotes, setLineNotes] = useState("");

  const [submitting, setSubmitting] = useState(false);

  // --- History ---
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // --- Clôture de journée ---
  const [closeDayOpen, setCloseDayOpen] = useState(false);
  const [closingDay, setClosingDay] = useState(false);
  const [lastClosure, setLastClosure] = useState(null);

  const actorRole = currentUser?.role || "coach_jeux";
  const actorName = currentUser?.full_name || currentUser?.username || "Coach";

  const selectedProduct = useMemo(
    () => catalog.find((p) => p.id === lineJeu),
    [catalog, lineJeu],
  );

  const cartTotal = useMemo(
    () => cart.reduce((sum, l) => sum + (Number(l.unit_price) || 0) * (Number(l.parties) || 0), 0),
    [cart],
  );

  const cartDuration = useMemo(
    () => cart.reduce((sum, l) => sum + (Number(l.duration_minutes) || 0), 0),
    [cart],
  );

  // Load catalog once
  const loadCatalog = useCallback(async () => {
    setLoadingCatalog(true);
    try {
      const r = await axios.get(`${API}/jeux/catalog`, { params: { actor_role: actorRole } });
      setCatalog(r.data.products || []);
    } catch (e) {
      toast.error("Erreur chargement catalogue");
    } finally {
      setLoadingCatalog(false);
    }
  }, [actorRole]);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  // Auto-fill price when product selection changes
  useEffect(() => {
    if (selectedProduct) setLinePrice(selectedProduct.price || 0);
  }, [selectedProduct]);

  const resetLineForm = () => {
    setLineJeu("");
    setLineParties(1);
    setLinePrice(0);
    setLineHours(1);
    setLineHourlyRate(DEFAULT_HOURLY_RATE);
    setLineDuration("");
    setLineNotes("");
    setBillingMode("parties");
  };

  const addLineToCart = () => {
    if (!selectedProduct) return toast.error("Sélectionnez un jeu");

    if (billingMode === "hourly") {
      const h = Number(lineHours) || 0;
      const rate = Number(lineHourlyRate) || 0;
      if (h <= 0) return toast.error("Nombre d'heures invalide");
      if (rate < 0) return toast.error("Tarif horaire invalide");
      const lineTotal = Math.round(h * rate);
      // Stockage: parties=1 (placeholder), unit_price=lineTotal (pour total)
      setCart((c) => [...c, {
        jeu_product_id: selectedProduct.id,
        jeu_name: selectedProduct.name,
        parties: 1,
        unit_price: lineTotal,
        duration_minutes: Math.round(h * 60),
        notes: lineNotes.trim(),
        billing_mode: "hourly",
        hours: h,
        hourly_rate: rate,
      }]);
      resetLineForm();
      toast.success(`Forfait ajouté : ${selectedProduct.name} · ${h}h`);
      return;
    }

    // Mode parties
    const p = Number(lineParties) || 0;
    if (p < 1) return toast.error("Nombre de parties invalide");
    if (Number(linePrice) < 0) return toast.error("Prix invalide");
    setCart((c) => [...c, {
      jeu_product_id: selectedProduct.id,
      jeu_name: selectedProduct.name,
      parties: p,
      unit_price: Number(linePrice),
      duration_minutes: lineDuration ? Number(lineDuration) : null,
      notes: lineNotes.trim(),
      billing_mode: "parties",
    }]);
    resetLineForm();
    toast.success(`Ligne ajoutée : ${selectedProduct.name} x${p}`);
  };

  const removeLine = (idx) => {
    setCart((c) => c.filter((_, i) => i !== idx));
  };

  // Load history when switching tab
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const r = await axios.get(`${API}/jeux/bons`, {
        params: { actor_role: actorRole, actor_name: actorName, limit: 100 },
      });
      setHistory(r.data.bons || []);
    } catch {
      toast.error("Erreur chargement historique");
    } finally {
      setLoadingHistory(false);
    }
  }, [actorRole, actorName]);

  useEffect(() => {
    // Charge l'historique au mount pour alimenter les KPIs du header,
    // puis à chaque visite de l'onglet Mes bons pour rafraîchir.
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    if (activeTab === "history") loadHistory();
  }, [activeTab, loadHistory]);

  const handleSubmit = async () => {
    if (cart.length === 0) return toast.error("Ajoutez au moins une ligne au bon");
    setSubmitting(true);
    try {
      await axios.post(`${API}/jeux/bons`, {
        items: cart,
        players: players.trim(),
        notes: bonNotes.trim(),
        coach_name: actorName,
        coach_role: actorRole,
      });
      toast.success(`Bon transmis au Responsable (${cart.length} ligne${cart.length > 1 ? "s" : ""})`);
      // Reset
      setCart([]);
      setPlayers("");
      setBonNotes("");
      resetLineForm();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Clôture de journée Coach ──
  const closeDay = async () => {
    setClosingDay(true);
    try {
      const r = await axios.post(`${API}/jeux/close-day`, {
        actor_role: actorRole,
        actor_name: actorName,
      }, { timeout: 30000 });
      const closure = r.data?.closure || null;
      setLastClosure(closure);
      toast.success(`Journée clôturée — ${closure?.total_bons || 0} bon(s) transmis à l'administrateur`);
      // Refresh history (qui se videra)
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la clôture");
    } finally {
      setClosingDay(false);
      setCloseDayOpen(false);
    }
  };

  // KPIs du jour pour le dashboard chunky
  const todayStats = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const todays = history.filter((b) => (b.created_at || "").slice(0, 10) === today);
    return {
      total: todays.length,
      pending: todays.filter((b) => b.status === "pending").length,
      validated: todays.filter((b) => b.status === "attached" || b.status === "invoiced").length,
      rejected: todays.filter((b) => b.status === "rejected").length,
      revenue: todays.reduce((s, b) => s + (b.total || 0), 0),
    };
  }, [history]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950/30 to-slate-900 text-white">
      <div className="max-w-5xl mx-auto p-3 sm:p-4">
        {/* ── HEADER PRO INDUSTRIEL ── */}
        <div className="rounded-xl bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 border-2 border-purple-500/40 shadow-2xl shadow-purple-500/10 p-3 sm:p-4 mb-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg bg-gradient-to-br from-purple-500 to-fuchsia-600 ring-2 ring-purple-400/50 shadow-lg shadow-purple-500/30 flex items-center justify-center">
                <Gamepad2 className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
              </div>
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-black uppercase tracking-wider truncate">
                  Coach Jeux <span className="text-purple-300">·</span> {actorName}
                  {currentUser?.is_guest && (
                    <span
                      className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-500/20 border border-rose-500/50 text-rose-200 align-middle normal-case tracking-normal"
                      title="Session Invité — valide jusqu'à révocation ou fin de journée"
                      data-testid="guest-session-badge"
                    >
                      <KeyRound className="w-3 h-3" /> INVITÉ
                    </span>
                  )}
                </h1>
                <p className="text-[10px] sm:text-xs text-purple-300/80 font-semibold uppercase tracking-widest">
                  {format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button
                size="sm"
                onClick={() => setCloseDayOpen(true)}
                disabled={closingDay}
                className="bg-rose-600 hover:bg-rose-700 text-white h-10 px-3 sm:px-4 font-bold text-[11px] sm:text-xs uppercase tracking-wider"
                data-testid="coach-close-day-btn"
                title="Clôturer la journée et transmettre les bons à l'administrateur"
              >
                {closingDay ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <><Lock className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Clôturer</span></>
                )}
              </Button>
              <Button variant="ghost" size="sm" onClick={onLogout}
                      className="text-rose-400 hover:text-rose-300 h-10 w-10 p-0" data-testid="coach-jeux-logout">
                <LogOut className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {/* KPIs Dashboard chunky du jour */}
          <div className="grid grid-cols-4 gap-2 mt-3" data-testid="coach-kpis">
            <div className="bg-slate-900/60 border border-amber-500/30 rounded-lg p-2 text-center" data-testid="kpi-pending">
              <p className="text-[9px] text-amber-300/80 uppercase tracking-wider font-bold">En attente</p>
              <p className="text-2xl sm:text-3xl font-black text-amber-300 leading-none mt-1">{todayStats.pending}</p>
            </div>
            <div className="bg-slate-900/60 border border-emerald-500/30 rounded-lg p-2 text-center" data-testid="kpi-validated">
              <p className="text-[9px] text-emerald-300/80 uppercase tracking-wider font-bold">Validés</p>
              <p className="text-2xl sm:text-3xl font-black text-emerald-300 leading-none mt-1">{todayStats.validated}</p>
            </div>
            <div className="bg-slate-900/60 border border-rose-500/30 rounded-lg p-2 text-center" data-testid="kpi-rejected">
              <p className="text-[9px] text-rose-300/80 uppercase tracking-wider font-bold">Refusés</p>
              <p className="text-2xl sm:text-3xl font-black text-rose-300 leading-none mt-1">{todayStats.rejected}</p>
            </div>
            <div className="bg-slate-900/60 border border-purple-500/30 rounded-lg p-2 text-center" data-testid="kpi-revenue">
              <p className="text-[9px] text-purple-300/80 uppercase tracking-wider font-bold">CA Jeux</p>
              <p className="text-base sm:text-xl font-black text-purple-300 leading-none mt-1">
                {Math.round(todayStats.revenue / 1000)}<span className="text-[10px] text-purple-400/70 ml-0.5">k F</span>
              </p>
            </div>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Navigation Pro Coach Jeux — même style que Kitchen Command Center :
              tuiles segmentées chunky, icônes proéminentes, badges absolus,
              état actif coloré par signature de l'onglet. */}
          {(() => {
            const MENU = [
              { id: "players",     label: "Joueurs",      short: "Joue",  icon: Users,        color: "slate",   testid: "coach-tab-players" },
              { id: "journal",     label: "Journal",      short: "Jrnl",  icon: Activity,     color: "zinc",    testid: "coach-tab-journal" },
              { id: "history",     label: "Mes bons",     short: "Bons",  icon: History,      color: "stone",   testid: "coach-tab-history",     badge: history.filter((b) => b.status === "pending").length, badgePulse: false },
              { id: "report",      label: "Rapport",      short: "Rpt",   icon: FileText,     color: "slateB",  testid: "coach-tab-report" },
              { id: "recoupement", label: "Recoupement",  short: "Rcpt",  icon: Trophy,       color: "teal",    testid: "coach-tab-recoupement" },
            ];
            const COLOR_MAP = {
              // Palette SOBRE & PRO — tons neutres élégants. Le préfixe `!` force la priorité
              // sur les classes data-[state=active]:bg-background de shadcn TabsTrigger.
              slate:   { active: "!bg-slate-300 !text-slate-950 shadow-slate-300/40",  bar: "bg-slate-300",  inactive: "text-slate-400/80 group-hover:text-slate-200"  },
              zinc:    { active: "!bg-zinc-300 !text-zinc-950 shadow-zinc-300/40",     bar: "bg-zinc-300",   inactive: "text-zinc-400/80 group-hover:text-zinc-200"    },
              stone:   { active: "!bg-stone-300 !text-stone-950 shadow-stone-300/40",  bar: "bg-stone-300",  inactive: "text-stone-400/80 group-hover:text-stone-200"  },
              slateB:  { active: "!bg-slate-500 !text-slate-50 shadow-slate-500/40",   bar: "bg-slate-500",  inactive: "text-slate-500/90 group-hover:text-slate-300"  },  // gris bleu
              teal:    { active: "!bg-teal-600 !text-slate-50 shadow-teal-600/40",     bar: "bg-teal-600",   inactive: "text-teal-600/90 group-hover:text-teal-300"    },  // gris vert
            };
            return (
              <div className="mb-5 -mx-1 px-1 overflow-x-auto scrollbar-thin">
                <TabsList className="bg-transparent border-0 grid grid-cols-3 md:grid-cols-5 gap-2 h-auto p-0 w-full">
                  {MENU.map((m) => {
                    const Icon = m.icon;
                    const isActive = activeTab === m.id;
                    const c = COLOR_MAP[m.color];
                    return (
                      <TabsTrigger
                        key={m.id}
                        value={m.id}
                        data-testid={m.testid}
                        className={`
                          group relative rounded-xl overflow-hidden p-0 h-auto transition-all duration-200
                          border-2 ${isActive ? `${c.active} font-black shadow-lg border-transparent` : 'bg-slate-900/80 border-slate-800 text-slate-400 hover:bg-slate-800/60 hover:text-white hover:border-slate-700'}
                          active:scale-95
                        `}
                      >
                        <div className="w-full px-2 py-3 flex flex-col items-center gap-1.5">
                          <div className="relative">
                            <Icon className={`w-6 h-6 sm:w-7 sm:h-7 ${isActive ? '' : c.inactive}`} strokeWidth={isActive ? 2.5 : 2} />
                            {!!m.badge && m.badge > 0 && (
                              <span
                                data-testid={`${m.testid}-badge`}
                                className={`absolute -top-2 -right-2 min-w-[22px] h-[22px] px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-black flex items-center justify-center ring-2 ring-slate-950 shadow-lg ${m.badgePulse ? 'animate-pulse' : ''}`}
                              >
                                {m.badge > 99 ? '99+' : m.badge}
                              </span>
                            )}
                          </div>
                          <span className={`text-xs sm:text-sm uppercase tracking-wider font-bold leading-tight text-center ${isActive ? '' : 'text-slate-200'}`}>
                            {m.label}
                          </span>
                        </div>
                        {/* Barre active en bas — accent visuel */}
                        {isActive && <div className={`absolute bottom-0 left-0 right-0 h-1 ${c.bar}`} />}
                      </TabsTrigger>
                    );
                  })}
                </TabsList>
              </div>
            );
          })()}

          {/* MES BONS */}
          <TabsContent value="history" className="mt-3">
            <Card className="bg-slate-800/60 border-blue-500/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <History className="w-5 h-5 text-blue-400" />
                    Historique de mes bons
                  </span>
                  <Button variant="ghost" size="sm" onClick={loadHistory} disabled={loadingHistory}
                          className="text-slate-300 h-7 text-[11px]" data-testid="coach-history-refresh">
                    {loadingHistory ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Actualiser
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {history.length === 0 && !loadingHistory && (
                  <p className="text-slate-500 italic text-center py-6">
                    Aucun bon transmis pour l&apos;instant.
                  </p>
                )}
                {history.map((b) => {
                  const meta = STATUS_META[b.status] || STATUS_META.pending;
                  const Icon = meta.icon;
                  // Compat: anciens bons mono-jeu
                  const items = b.items && b.items.length
                    ? b.items
                    : (b.jeu_product_id ? [{ jeu_name: b.jeu_name, parties: b.parties, total: b.total, duration_minutes: b.duration_minutes }] : []);
                  return (
                    <div key={b.id}
                         className={`rounded border ${meta.color} p-2.5`}
                         data-testid={`coach-bon-${b.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap mb-1">
                            <Receipt className="w-3.5 h-3.5 shrink-0" />
                            <span className="font-semibold text-sm">Bon · {items.length} ligne{items.length > 1 ? "s" : ""}</span>
                            <Badge className="bg-emerald-700/50 text-emerald-100 text-[10px]">
                              {(b.total || 0).toLocaleString("fr-FR")} F
                            </Badge>
                          </div>
                          {/* Lignes */}
                          <div className="space-y-0.5 ml-1">
                            {items.map((it, idx) => {
                              const isH = it.billing_mode === "hourly";
                              return (
                              <div key={idx} className="text-[11px] text-slate-300 flex items-center gap-1.5 flex-wrap">
                                <Gamepad2 className={`w-3 h-3 ${isH ? "text-amber-400" : "text-purple-400"}`} />
                                <span>{it.jeu_name}</span>
                                {isH ? (
                                  <Badge className="bg-amber-700/50 text-amber-100 text-[9px]">Forfait {it.hours}h</Badge>
                                ) : (
                                  <Badge className="bg-slate-700 text-slate-200 text-[9px]">x{it.parties}</Badge>
                                )}
                                <span className="text-slate-400">{(it.total || 0).toLocaleString("fr-FR")} F</span>
                                {!isH && it.duration_minutes && <span className="text-slate-500">· {it.duration_minutes} min</span>}
                              </div>
                              );
                            })}
                          </div>
                          {b.players && (
                            <p className="text-[11px] text-slate-300 mt-1">
                              <Users className="w-3 h-3 inline mr-1" /> {b.players}
                            </p>
                          )}
                          {b.notes && <p className="text-[10px] text-slate-400 italic mt-0.5">{b.notes}</p>}
                        </div>
                        <Badge className={`${meta.color} text-[10px] border shrink-0`}>
                          <Icon className="w-3 h-3 mr-1" /> {meta.label}
                        </Badge>
                      </div>
                      <div className="text-[10px] text-slate-400 mt-1 flex flex-wrap gap-x-3">
                        <span>Créé : {b.created_at ? format(new Date(b.created_at), "HH:mm") : "—"}</span>
                        {b.status === "attached" && b.table_number != null && (
                          <span className="text-blue-300">→ Table {b.table_number}</span>
                        )}
                        {b.status === "invoiced" && b.invoice_number && (
                          <span className="text-emerald-300">→ Facture {b.invoice_number}</span>
                        )}
                        {b.status === "rejected" && b.rejection_reason && (
                          <span className="text-rose-300">Motif : {b.rejection_reason}</span>
                        )}
                        {b.processed_by && <span>par {b.processed_by}</span>}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </TabsContent>

          {/* RAPPORT DU JOUR */}
          <TabsContent value="report" className="mt-3">
            <DailyReportPanel currentUser={currentUser} kind="coach_jeux" />
          </TabsContent>

          {/* JOUEURS — Suivi consommation par joueur */}
          <TabsContent value="players" className="mt-3">
            <PlayersTrackerTab currentUser={currentUser} catalog={catalog} />
          </TabsContent>

          {/* JOURNAL — Timeline horodatée de la séance */}
          <TabsContent value="journal" className="mt-3">
            <JournalTab currentUser={currentUser} />
          </TabsContent>

          {/* RECOUPEMENT — comparaison jeux déclarés (coach) vs facturés (caisse) */}
          <TabsContent value="recoupement" className="mt-3">
            <RecoupementSyntheseTable kind="jeux" title="Mes jeux : déclarés vs facturés caisse" />
          </TabsContent>
        </Tabs>

        {/* MODAL — Clôture de journée Coach */}
        <Dialog open={closeDayOpen} onOpenChange={setCloseDayOpen}>
          <DialogContent className="bg-slate-900 border-rose-500/40 text-white max-w-md" data-testid="coach-close-day-modal">
            <DialogHeader>
              <DialogTitle className="text-lg flex items-center gap-2 text-rose-200">
                <Lock className="w-5 h-5" />
                Clôturer la journée
              </DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">
                Tous vos bons de la journée seront transmis à l&apos;administrateur dans
                <span className="text-purple-300 font-semibold"> « Rapports Cuisine &amp; Jeux »</span>.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <div className="bg-slate-800/60 border border-rose-500/30 rounded-lg p-3">
                <p className="text-slate-300 mb-2">Après clôture :</p>
                <ul className="text-xs space-y-1 text-slate-400">
                  <li>• L&apos;onglet <strong className="text-blue-300">Mes bons</strong> sera vidé</li>
                  <li>• Le journal &amp; les joueurs repartent à zéro</li>
                  <li>• L&apos;administrateur conservera toutes les données pour ses rapports</li>
                </ul>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5 text-xs text-amber-200">
                <Bell className="w-3.5 h-3.5 inline mr-1" />
                Cette action est <strong>définitive</strong> côté Coach. Confirmez uniquement en fin de service.
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="ghost" onClick={() => setCloseDayOpen(false)} disabled={closingDay}
                      className="text-slate-300 hover:bg-slate-800 border border-slate-700"
                      data-testid="coach-close-day-cancel">
                Annuler
              </Button>
              <Button onClick={closeDay} disabled={closingDay}
                      className="bg-rose-600 hover:bg-rose-700 text-white font-bold"
                      data-testid="coach-close-day-confirm">
                {closingDay ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Clôture en cours…</>
                ) : (
                  <><Lock className="w-4 h-4 mr-2" /> Confirmer la clôture</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* MODAL — Résumé de la dernière clôture */}
        <Dialog open={!!lastClosure} onOpenChange={(o) => { if (!o) setLastClosure(null); }}>
          <DialogContent className="bg-slate-900 border-emerald-500/40 text-white max-w-md" data-testid="coach-closure-summary-modal">
            <DialogHeader>
              <DialogTitle className="text-lg flex items-center gap-2 text-emerald-200">
                <CheckCircle2 className="w-5 h-5" />
                Journée clôturée avec succès
              </DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">
                Vos données ont été transmises à l&apos;administrateur.
              </DialogDescription>
            </DialogHeader>
            {lastClosure && (
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700">
                  <p className="text-2xl font-black text-blue-300">{lastClosure.total_bons}</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Bons</p>
                </div>
                <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700">
                  <p className="text-2xl font-black text-purple-300">{lastClosure.total_parties}</p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">Parties</p>
                </div>
                <div className="bg-slate-800/60 rounded-lg p-3 border border-slate-700">
                  <p className="text-xl font-black text-emerald-300">
                    {Math.round((lastClosure.total_revenue || 0) / 1000)}<span className="text-[10px] ml-0.5">k F</span>
                  </p>
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">CA Jeux</p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button onClick={() => setLastClosure(null)}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white w-full"
                      data-testid="coach-closure-summary-close">
                Fermer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CoachJeuxPage;
